import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

export const COLLAPSIBLE_ITEMS_CONTENT: Partial<
  Record<
    ModelTypeConstants,
    Array<{
      icon: BenchlingIconName;
      title: string;
      carouselItems: Array<{description: string; imageSrc: string}>;
    }>
  >
> = {
  [ModelTypeConstants.CHAT_SESSION]: [
    {
      icon: 'chat',
      title: 'Deep Research',
      carouselItems: [
        {
          description: 'Use Benchling AI to answer questions about your data.',
          imageSrc: '/static/img/shared_file_messaging/deep_research.png',
        },
        {
          description: 'Produce tables from unstructured and structured data.',
          imageSrc: '/static/img/shared_file_messaging/deep_research_table.png',
        },
      ],
    },
  ],
  [ModelTypeConstants.SEQUENCE]: [
    {
      icon: 'alignment',
      title: 'Create an alignment',
      carouselItems: [
        {
          description: 'Create alignments with both DNA and AA sequences.',
          imageSrc: '/static/img/shared_file_messaging/DNA-1-A.png',
        },
        {
          description: 'Bulk-align multiple sequences at once.',
          imageSrc: '/static/img/shared_file_messaging/DNA-1-B.png',
        },
        {
          description: 'Powerful visualization tool to inspect alignments in detail.',
          imageSrc: '/static/img/shared_file_messaging/DNA-1-C.png',
        },
      ],
    },
    {
      icon: ModelTypeConstants.SEQUENCE_PRIMER,
      title: 'Design a primer',
      carouselItems: [
        {
          description: 'Create primers manually, or using a wizard.',
          imageSrc: '/static/img/shared_file_messaging/DNA-2-A.png',
        },
        {
          description: 'Attach primers to a DNA sequence of interest.',
          imageSrc: '/static/img/shared_file_messaging/DNA-2-B.png',
        },
        {
          description: 'Review and fine-tune primer properties.',
          imageSrc: '/static/img/shared_file_messaging/DNA-2-C.png',
        },
      ],
    },
    {
      icon: 'protein-alternative',
      title: 'Create a translation',
      carouselItems: [
        {
          description: 'Create and visualize AA sequences.',
          imageSrc: '/static/img/shared_file_messaging/DNA-3-A.png',
        },
        {
          description: 'Attach translations to a DNA sequence of interest.',
          imageSrc: '/static/img/shared_file_messaging/DNA-3-B.png',
        },
        {
          description: 'Annotate translations using custom labels and colors.',
          imageSrc: '/static/img/shared_file_messaging/DNA-3-C.png',
        },
      ],
    },
    {
      icon: 'crispr',
      title: 'Design a CRISPR guide',
      carouselItems: [
        {
          description: 'Create CRISPR guides for a variety of use cases.',
          imageSrc: '/static/img/shared_file_messaging/DNA-4-A.png',
        },
        {
          description: 'Evaluate guides using off-target and on-target scores.',
          imageSrc: '/static/img/shared_file_messaging/DNA-4-B.png',
        },
        {
          description: 'Find matching genome regions automatically.',
          imageSrc: '/static/img/shared_file_messaging/DNA-4-C.png',
        },
      ],
    },
    {
      icon: 'assembly-v2',
      title: 'Assemble and transform a sequence',
      carouselItems: [
        {
          description: 'Clone sequences using the digest & ligate wizard.',
          imageSrc: '/static/img/shared_file_messaging/DNA-5-A.png',
        },
        {
          description: 'Create Gibson & Golden Gate assemblies.',
          imageSrc: '/static/img/shared_file_messaging/DNA-5-B.png',
        },
        {
          description: 'Visualize and annotate constructs.',
          imageSrc: '/static/img/shared_file_messaging/DNA-5-C.png',
        },
      ],
    },
  ],
  [ModelTypeConstants.PROTEIN]: [
    {
      icon: ModelTypeConstants.SEQUENCE_PRIMER,
      title: 'Explore an AA sequence',
      carouselItems: [
        {
          description: 'Automatically calculate common biochemical properties.',
          imageSrc: '/static/img/shared_file_messaging/AA-1-A.png',
        },
        {
          description: 'Preview liability sites for each sequence.',
          imageSrc: '/static/img/shared_file_messaging/AA-1-B.png',
        },
        {
          description: 'Identify complementarity-determining (CDR) regions.',
          imageSrc: '/static/img/shared_file_messaging/AA-1-C.png',
        },
      ],
    },
    {
      icon: 'alignment',
      title: 'Create an alignment',
      carouselItems: [
        {
          description: 'Create alignments with both DNA and AA sequences.',
          imageSrc: '/static/img/shared_file_messaging/AA-2-A.png',
        },
        {
          description: 'Bulk-align multiple sequences at once.',
          imageSrc: '/static/img/shared_file_messaging/AA-2-B.png',
        },
        {
          description: 'Powerful visualization tool to inspect alignments in detail.',
          imageSrc: '/static/img/shared_file_messaging/AA-2-C.png',
        },
      ],
    },
    {
      icon: 'helix',
      title: 'Create back translations',
      carouselItems: [
        {
          description: 'Optimize codons using DNAChisel.',
          imageSrc: '/static/img/shared_file_messaging/AA-3-A.png',
        },
        {
          description: 'Specify cut sites to avoid.',
          imageSrc: '/static/img/shared_file_messaging/AA-3-B.png',
        },
        {
          description: 'Minimize the usage of specified patterns.',
          imageSrc: '/static/img/shared_file_messaging/AA-3-C.png',
        },
      ],
    },
  ],
  [ModelTypeConstants.ENTRY]: [
    {
      icon: 'eln-timestamp',
      title: 'Track every step of your experiment',
      carouselItems: [
        {
          description: 'Add new dates for every day your experiment runs.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-1-A.png',
        },
        {
          description: 'Capture protocols, observations, and tasks.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-1-B.png',
        },
        {
          description: 'Automatically keep a detailed history of your work.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-1-C.png',
        },
        {
          description: 'Auto-populate your calendar with Notebook entries.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-1-D.png',
        },
      ],
    },
    {
      icon: 'computed-value',
      title: 'Insert tables with calculations',
      carouselItems: [
        {
          description: 'Add tables alongside your notes.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-2-A.png',
        },
        {
          description: 'Use formulas to calculate master mixes.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-2-B.png',
        },
        {
          description: 'Capture results from your experiments.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-2-C.png',
        },
      ],
    },
    {
      icon: 'at-mention',
      title: 'Link sequences directly in your entry',
      carouselItems: [
        {
          description: '@mention sequences relevant to your experiments.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-3-A.png',
        },
        {
          description: 'Visualize sequences alongside your notes.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-3-B.png',
        },
        {
          description: 'Go to Molecular Biology to design & analyze sequences.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-3-C.png',
        },
      ],
    },
    {
      icon: 'attachment',
      title: 'Attach images and results',
      carouselItems: [
        {
          description: 'Upload experimental results to Benchling.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-4-A.png',
        },
        {
          description: 'Attach and preview other file types.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-4-B.png',
        },
        {
          description: 'Link your cloud storage platforms to Benchling.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-4-C.png',
        },
      ],
    },
    {
      icon: 'share-v2',
      title: 'Share data with your collaborators',
      carouselItems: [
        {
          description: 'Create projects, and share them with your lab.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-5-A.png',
        },
        {
          description: 'Share entries with individual collaborators.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-5-B.png',
        },
        {
          description: '@mention collaborators in your Notebook entries.',
          imageSrc: '/static/img/shared_file_messaging/NB-entry-5-C.png',
        },
      ],
    },
  ],
  [ModelTypeConstants.PROTOCOL]: [
    {
      icon: 'note',
      title: 'Capture context, materials, and steps',
      carouselItems: [
        {
          description: 'Document a list of materials.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-1-A.png',
        },
        {
          description: 'Add individual steps in the protocol.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-1-B.png',
        },
        {
          description: 'Add timers directly within your protocol.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-1-C.png',
        },
      ],
    },
    {
      icon: 'folder-open',
      title: 'Create protocol repositories for your lab',
      carouselItems: [
        {
          description: 'Create projects to house protocols for your lab.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-2-A.png',
        },
        {
          description: 'Share and collaborate on protocols with your lab.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-2-B.png',
        },
        {
          description: 'Share protocols with individual collaborators.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-2-C.png',
        },
      ],
    },
    {
      icon: 'computed-value',
      title: 'Insert tables with calculations',
      carouselItems: [
        {
          description: 'Add tables alongside your notes.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-3-A.png',
        },
        {
          description: 'Set up and follow along with recipes.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-3-B.png',
        },
        {
          description: 'Perform calculations within and across tables.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-3-C.png',
        },
      ],
    },
    {
      icon: 'attachment',
      title: 'Attach protocols to Notebook entries',
      carouselItems: [
        {
          description: 'Link protocols to relevant experiments.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-4-A.png',
        },
        {
          description: 'Search all of your protocols at once.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-4-B.png',
        },
        {
          description: 'Follow along with protocols and entries side-by-side.',
          imageSrc: '/static/img/shared_file_messaging/Protocol-4-C.png',
        },
      ],
    },
  ],
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: [
    {
      icon: 'note',
      title: 'Take notes on your experiments',
      carouselItems: [
        {
          description: 'Capture protocols, observations, and tasks.',
          imageSrc: '/static/img/shared_file_messaging/Custom-1-A.png',
        },
        {
          description: 'Add new dates for every day your experiment runs.',
          imageSrc: '/static/img/shared_file_messaging/Custom-1-B.png',
        },
        {
          description: 'Capture results from your experiments.',
          imageSrc: '/static/img/shared_file_messaging/Custom-1-C.png',
        },
      ],
    },
    {
      icon: FolderItemIcon.Sequence,
      title: 'Design and analyze sequences',
      carouselItems: [
        {
          description: 'Create and visualize DNA sequences.',
          imageSrc: '/static/img/shared_file_messaging/Custom-2-A.png',
        },
        {
          description: 'Attach primers to a DNA sequence of interest.',
          imageSrc: '/static/img/shared_file_messaging/Custom-2-B.png',
        },
        {
          description: 'Create and visualize AA sequences.',
          imageSrc: '/static/img/shared_file_messaging/Custom-2-C.png',
        },
        {
          description: 'Create alignments with both DNA and AA sequences.',
          imageSrc: '/static/img/shared_file_messaging/Custom-2-D.png',
        },
      ],
    },
    {
      icon: 'folder-open',
      title: 'Organize your work',
      carouselItems: [
        {
          description: 'Create projects and share them for your lab.',
          imageSrc: '/static/img/shared_file_messaging/Custom-3-A.png',
        },
        {
          description: 'Organize your data into folders.',
          imageSrc: '/static/img/shared_file_messaging/Custom-3-B.png',
        },
        {
          description: 'Search across all your sequences and experiments.',
          imageSrc: '/static/img/shared_file_messaging/Custom-3-C.png',
        },
      ],
    },
    {
      icon: 'attachment',
      title: 'Track all your data',
      carouselItems: [
        {
          description: 'Automatically keep a detailed version history of your work.',
          imageSrc: '/static/img/shared_file_messaging/Custom-4-A.png',
        },
        {
          description: 'Control permissions for viewing and editing.',
          imageSrc: '/static/img/shared_file_messaging/Custom-4-B.png',
        },
      ],
    },
  ],
};
